import { Stack } from "@chakra-ui/react";
import { Dispatch, FC, SetStateAction, useEffect } from "react";
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import InputFieldComponent from "../../../components/molecule/InputField/InputFieldComponent";
import LabelComponent from "../../../components/atom/Label/LabelComponent";
import PickList from "../../../components/template/PickList/PickList";
import { getRoleLevels } from "../../../services/getRoleLevels";
import FormSelect from "../../../components/molecule/Select/FormSelect";
import useFeaturesHandler from "../handler/getFeaturesHandler";

interface RolesSubmitFormProps {
  errors: FieldErrors<FieldValues>;
  control: Control<FieldValues, any>;
  watch: UseFormWatch<FieldValues>;
  isDisabled?: boolean;
  setValue: UseFormSetValue<FieldValues>;
  hasInteracted: boolean;
  setHasInteracted: Dispatch<SetStateAction<boolean>>;
  rolePermissions?: any[];
  organizationLevelOptionSelected?: number;
}

const RolesSubmitForm: FC<RolesSubmitFormProps> = ({
  errors,
  control,
  watch,
  isDisabled,
  setValue,
  hasInteracted,
  setHasInteracted,
  rolePermissions,
  organizationLevelOptionSelected,
}) => {
  const organizationLevelOption = watch("organizationLevelOption");

  const {
    hasLoadedFeatures,
    featuresOptions,
    selectedPermissionOptions,
    originalFeaturesDataTable,
    originalSelectedPermissionsDataTable,
    setFeaturesOptions,
    setSelectedPermissionOptions,
    setOriginalFeaturesDataTable,
    setOriginalSelectedPermissionsDataTable,
    getFeaturesOptions,
  } = useFeaturesHandler(rolePermissions, organizationLevelOptionSelected);

  useEffect(() => {
    if (organizationLevelOption) {
      setOriginalFeaturesDataTable([]);
      setFeaturesOptions([]);
      setOriginalSelectedPermissionsDataTable([]);
      setSelectedPermissionOptions([]);
      getFeaturesOptions(organizationLevelOption.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationLevelOption]);

  useEffect(() => {
    const permissionCodes = selectedPermissionOptions.map((option) =>
      option.code.toString(),
    );
    setValue("permissions", permissionCodes, { shouldValidate: true });
  }, [selectedPermissionOptions, setValue]);

  return (
    <Stack gap={4}>
      <InputFieldComponent
        label="Nome"
        name="name"
        type="text"
        register={control.register}
        errors={errors}
        isRequired
        isDisabled={isDisabled}
      />
      <FormSelect
        control={control}
        errors={errors}
        watch={watch}
        fieldName="organizationLevelOption"
        fieldLabel="Tipo de perfil"
        fetchOptions={getRoleLevels}
        isDisabled={isDisabled}
      />
      {organizationLevelOption && (
        <Stack gap={0}>
          <LabelComponent
            title="Adicionar e remover privilégios"
            fontWeight="500"
            fontSize="md"
          ></LabelComponent>

          <PickList
            hasLoadedFeatures={hasLoadedFeatures}
            featuresOptions={featuresOptions}
            setFeaturesOptions={setFeaturesOptions}
            selectedFeaturesOptions={selectedPermissionOptions}
            setSelectedFeaturesOptions={setSelectedPermissionOptions}
            originalFeaturesDataTable={originalFeaturesDataTable}
            setOriginalFeaturesDataTable={setOriginalFeaturesDataTable}
            originalSelectedFeaturesDataTable={
              originalSelectedPermissionsDataTable
            }
            setOriginalSelectedFeaturesDataTable={
              setOriginalSelectedPermissionsDataTable
            }
            featuresTitle="Privilégios disponíveis"
            selectedFeaturesTitle="Privilégios activos"
            isDisabled={isDisabled}
            errors={errors}
            hasInteracted={hasInteracted}
            setHasInteracted={setHasInteracted}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default RolesSubmitForm;
